import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Bot,
  Filter,
  Send,
  Shield,
  Upload,
  Workflow,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const capabilities = [
  {
    icon: Bot,
    title: "AI Conversation Flow",
    text: "Create adaptive and contextual reply journeys without complex coding.",
  },
  {
    icon: Send,
    title: "Bulk Messaging",
    text: "Reach thousands instantly with controlled delivery and smart template usage.",
  },
  {
    icon: Workflow,
    title: "Smart Automation",
    text: "Trigger workflows for reminders, follow-ups, renewals, and support updates.",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    text: "Track delivery, reads, and conversions to optimize every campaign cycle.",
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    text: "Keep communication safe with reliable delivery and compliance-friendly operations.",
  },
  {
    icon: Filter,
    title: "Precision Targeting",
    text: "Build segments with filters and tags to send relevant messages every time.",
  },
];

const methods = [
  {
    icon: Upload,
    title: "CSV Upload",
    text: "Upload contact sheets and map variables quickly for fast campaign launches.",
  },
  {
    icon: Workflow,
    title: "Copy-Paste Grid",
    text: "Paste rows from Excel or Google Sheets and edit before sending.",
  },
  {
    icon: Filter,
    title: "Saved Contact Filters",
    text: "Target the right segment with reusable filters based on tags and behavior.",
  },
];

const Features = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  return (
    <section id="features" className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-700">
            Enterprise-grade WhatsApp solutions
          </p>
          <h2 className="mt-3 text-3xl font-black leading-tight text-gray-900 sm:text-5xl">
            Everything You Need To Scale Conversations
          </h2>
          <p className="mt-4 text-base text-gray-600 sm:text-lg">
            Keep your existing workflows and upgrade how fast, reliable, and
            personalized your WhatsApp communication can be.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((item, idx) => (
            <motion.article
              key={item.title}
              initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: idx * 0.06 }}
              className="rounded-2xl border border-amber-100 bg-gradient-to-b from-white to-amber-50/30 p-6"
            >
              <item.icon className="h-6 w-6 text-orange-500" />
              <h3 className="mt-4 text-xl font-bold text-gray-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.text}</p>
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-orange-600"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </button>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-amber-200 bg-gradient-to-r from-amber-50/80 to-orange-50/80 p-6 sm:p-10">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-700">
                Three ways to send
              </p>
              <h3 className="mt-2 text-2xl font-black text-gray-900 sm:text-3xl">
                Choose The Method That Fits Your Team
              </h3>
            </div>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white"
            >
              Start Sending
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {methods.map((method, idx) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="rounded-2xl border border-amber-100 bg-white p-5"
              >
                <method.icon className="h-5 w-5 text-amber-700" />
                <h4 className="mt-3 text-lg font-bold text-gray-900">{method.title}</h4>
                <p className="mt-2 text-sm text-gray-600">{method.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;